import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, FileText, Save, GripVertical, Globe } from "lucide-react";
import { toast } from "sonner";

const LANGS = [
  { code: "it", label: "Italiano" },
  { code: "en", label: "English" },
];

export default function AdminContractTemplate() {
  const { i18n } = useTranslation();
  const isEN = i18n.language === "en";
  const [tpl, setTpl] = useState(null);
  const [activeLang, setActiveLang] = useState("it");
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(1);

  useEffect(() => {
    api.get("/admin/contract-template").then(({ data }) => {
      setTpl({
        title_it: data.title_it, title_en: data.title_en,
        intro_it: data.intro_it, intro_en: data.intro_en,
        footer_it: data.footer_it, footer_en: data.footer_en,
        sections_it: data.sections_it.map((s) => ({ ...s })),
        sections_en: data.sections_en.map((s) => ({ ...s })),
      });
      setVersion(data.version || 1);
    });
  }, []);

  if (!tpl) return <div className="text-zinc-400">Loading…</div>;

  const sections = tpl[`sections_${activeLang}`];
  const setSection = (i, key, value) => {
    const next = sections.map((s, idx) => idx === i ? { ...s, [key]: value } : s);
    setTpl({ ...tpl, [`sections_${activeLang}`]: next });
  };
  const addSection = () => {
    setTpl({ ...tpl, [`sections_${activeLang}`]: [...sections, { title: "", body: "", _uid: `s-${Date.now()}-${Math.random().toString(36).slice(2,7)}` }] });
  };
  const removeSection = (i) => {
    setTpl({ ...tpl, [`sections_${activeLang}`]: sections.filter((_, idx) => idx !== i) });
  };
  const moveSection = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    const next = [...sections];
    [next[i], next[j]] = [next[j], next[i]];
    setTpl({ ...tpl, [`sections_${activeLang}`]: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/contract-template", tpl);
      setVersion(data.version || version + 1);
      toast.success(isEN ? "Template saved" : "Modello salvato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="contract-template-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#FFD700]">{isEN ? "Legal" : "Legale"}</p>
          <h1 className="font-display text-4xl font-bold mt-1">
            {isEN ? "Contract template" : "Modello contratto"}
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            {isEN ? "Edit clauses. Changes apply to future PDFs; signed contracts keep their snapshot."
                  : "Modifica le clausole. Le modifiche valgono per i futuri PDF; i contratti firmati conservano la versione storica."}
          </p>
          <p className="text-xs text-zinc-600 mt-1">v.{version}</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold" data-testid="template-save">
          <Save className="w-4 h-4 mr-1" />
          {saving ? (isEN ? "Saving…" : "Salvataggio…") : (isEN ? "Save template" : "Salva modello")}
        </Button>
      </div>

      {/* Language tabs */}
      <div className="flex gap-1 border-b border-zinc-800">
        {LANGS.map((l) => (
          <button
            key={l.code}
            onClick={() => setActiveLang(l.code)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              activeLang === l.code ? "border-[#FFD700] text-[#FFD700]" : "border-transparent text-zinc-500 hover:text-zinc-300"
            }`}
            data-testid={`template-tab-${l.code}`}
          >
            <Globe className="w-3.5 h-3.5 inline mr-1" />
            {l.label}
          </button>
        ))}
      </div>

      {/* Title / intro / footer */}
      <div className="space-y-3 border border-zinc-800 rounded-xl p-5 bg-zinc-900/40">
        <div>
          <Label className="text-xs text-zinc-400">{isEN ? "Title" : "Titolo"}</Label>
          <Input value={tpl[`title_${activeLang}`]}
                 onChange={(e) => setTpl({ ...tpl, [`title_${activeLang}`]: e.target.value })}
                 className="bg-zinc-950 border-zinc-800 mt-1 font-semibold"
                 data-testid="template-title" />
        </div>
        <div>
          <Label className="text-xs text-zinc-400">{isEN ? "Intro paragraph" : "Paragrafo introduttivo"}</Label>
          <Textarea rows={3} value={tpl[`intro_${activeLang}`]}
                    onChange={(e) => setTpl({ ...tpl, [`intro_${activeLang}`]: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 mt-1"
                    data-testid="template-intro" />
        </div>
        <div>
          <Label className="text-xs text-zinc-400">{isEN ? "Footer" : "Footer (piè di pagina)"}</Label>
          <Textarea rows={2} value={tpl[`footer_${activeLang}`]}
                    onChange={(e) => setTpl({ ...tpl, [`footer_${activeLang}`]: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 mt-1"
                    data-testid="template-footer" />
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-zinc-200 flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#FFD700]" />
            {isEN ? "Clauses" : "Clausole"} ({sections.length})
          </h2>
          <Button onClick={addSection} variant="outline" size="sm" className="bg-zinc-950 border-zinc-700 hover:bg-zinc-800" data-testid="template-add-section">
            <Plus className="w-4 h-4 mr-1" /> {isEN ? "Add clause" : "Aggiungi clausola"}
          </Button>
        </div>

        {sections.map((s, i) => (
          <div key={s._uid || `legacy-${i}`} className="border border-zinc-800 rounded-xl p-4 bg-zinc-900/40 space-y-2" data-testid={`template-section-${i}`}>
            <div className="flex items-start gap-2">
              <div className="flex flex-col gap-0.5 mt-2 text-zinc-600">
                <button type="button" onClick={() => moveSection(i, -1)} disabled={i === 0} className="hover:text-zinc-300 disabled:opacity-20">▲</button>
                <GripVertical className="w-3 h-3" />
                <button type="button" onClick={() => moveSection(i, 1)} disabled={i === sections.length - 1} className="hover:text-zinc-300 disabled:opacity-20">▼</button>
              </div>
              <div className="flex-1 space-y-2">
                <Input value={s.title}
                       onChange={(e) => setSection(i, "title", e.target.value)}
                       placeholder={isEN ? "Clause title" : "Titolo clausola"}
                       className="bg-zinc-950 border-zinc-800 font-medium"
                       data-testid={`section-title-${i}`} />
                <Textarea value={s.body}
                          onChange={(e) => setSection(i, "body", e.target.value)}
                          placeholder={isEN ? "Clause body" : "Testo della clausola"}
                          rows={4}
                          className="bg-zinc-950 border-zinc-800 text-sm"
                          data-testid={`section-body-${i}`} />
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeSection(i)}
                      className="text-zinc-500 hover:text-red-400" data-testid={`section-remove-${i}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="sticky bottom-4 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold shadow-lg shadow-[#FFD700]/20">
          <Save className="w-4 h-4 mr-1" />
          {saving ? (isEN ? "Saving…" : "Salvataggio…") : (isEN ? "Save changes" : "Salva modifiche")}
        </Button>
      </div>
    </div>
  );
}
